import jsPDF from 'jspdf';
import { getBrandLocale } from './brandCurrency';
import i18n from '../../i18n';
import { buildReportPdfFilename } from './exportFilename';

// Import autoTable properly for newer versions
import autoTable from 'jspdf-autotable';

// Helper function to sanitize text for PDF compatibility
const sanitizeTextForPdf = (text) => {
  if (!text) return '';
  
  // Remove or replace problematic characters while keeping French accents and common symbols
  return text
    // First, handle specific problematic sequences
    .replace(/[þÞ–óîÔçÍÜØ=]/g, '') // Remove specific corrupted characters
    .replace(/[^\x20-\x7E\u00C0-\u017F]/g, '') // Keep printable ASCII + Latin Extended-A (includes French accents)
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

// Generate PDF for reports
// Helper function to extract data from a table element
const extractTableData = (tableElement) => {
  const headers = [];
  const rows = [];
  
  // Get headers
  const headerRow = tableElement.querySelector('thead tr');
  if (headerRow) {
    headerRow.querySelectorAll('th').forEach(th => {
      headers.push(sanitizeTextForPdf(th.textContent.trim()));
    });
  }
  
  // Get rows
  const bodyRows = tableElement.querySelectorAll('tbody tr');
  bodyRows.forEach(tr => {
    const rowData = [];
    tr.querySelectorAll('td').forEach(td => {
      rowData.push(sanitizeTextForPdf(td.textContent.trim()));
    });
    if (rowData.length > 0) {
      rows.push(rowData);
    }
  });
  
  return { headers, rows };
};

const reportLocale = () => getBrandLocale(i18n.language);

// Function to draw a table to PDF
const drawTableToPdf = (pdf, tableData, x, y, maxWidth) => {
  if (!tableData.headers.length && !tableData.rows.length) {
    pdf.text(i18n.t('common.no_data'), x, y);
    return;
  }
  
  autoTable(pdf, {
    startY: y,
    head: [tableData.headers],
    body: tableData.rows,
    margin: { left: x, right: x },
    tableWidth: maxWidth,
    styles: {
      fontSize: 9,
      cellPadding: 3,
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [248, 248, 248],
    },
    // Handle the TOTAL row specially
    didParseCell: function(data) {
      const lastRowIndex = tableData.rows.length - 1;
      const totalLabel = i18n.t('reports.total');
      if (data.row.index === lastRowIndex && tableData.rows[lastRowIndex][0].includes(totalLabel)) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [240, 240, 240];
      }
    }
  });
};

export const generateReportPDF = async (reportType, reportTitle, settings) => {
  try {
    console.log('Starting PDF generation for', reportType);
    
    // Find the specific report content based on the active tab
    let reportElement;
    
    // First try to find the specific report content
    if (reportType === 'tva') {
      reportElement = document.querySelector('.report-content table');
    } else if (reportType === 'articles') {
      reportElement = document.querySelector('.report-content table');
    } else if (reportType === 'categories') {
      reportElement = document.querySelector('.report-content .grid');
    }
    
    // If specific element not found, fall back to the entire report content
    if (!reportElement) {
      reportElement = document.querySelector('.report-content');
      console.log('Using fallback report content element');
    }
    
    if (!reportElement) {
      throw new Error(i18n.t('common.report_content_not_found'));
    }
    
    console.log('Found report element:', reportElement);

    // Instead of using html2canvas, we'll manually create the PDF content
    // Create a PDF document
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Add header
    addHeader(pdf, reportTitle, settings);
    
    // Set starting position after header (increased due to larger header)
    let yPosition = 65;
    const startX = 10;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const usableWidth = pageWidth - 20; // 10mm margin on each side
    
    // Get table data
    if (reportElement.tagName === 'TABLE') {
      // Process table directly
      const tableData = extractTableData(reportElement);
      drawTableToPdf(pdf, tableData, startX, yPosition, usableWidth);
    } 
    else if (reportType === 'categories') {
      // For categories report, find the table and chart
      const table = reportElement.querySelector('table');
      if (table) {
        const tableData = extractTableData(table);
        drawTableToPdf(pdf, tableData, startX, yPosition, usableWidth);
        yPosition += tableData.rows.length * 10 + 20; // Move down after table
        
        // Add note about pie chart
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'italic');
        pdf.text(i18n.t('common.report_chart_in_app'), startX, yPosition);
      }
    }
    else {
      // Try to find tables within the element
      const tables = reportElement.querySelectorAll('table');
      if (tables.length > 0) {
        tables.forEach(table => {
          const tableData = extractTableData(table);
          drawTableToPdf(pdf, tableData, startX, yPosition, usableWidth);
          yPosition += tableData.rows.length * 10 + 20; // Move down after table
        });
      } else {
        // Fallback - just add text saying no data
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text(i18n.t('common.report_no_data_for_report'), startX, yPosition);
      }
    }

    // Add footer
    addFooter(pdf, settings);

    const fileName = buildReportPdfFilename(reportType);
    pdf.save(fileName);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

// Add header to the PDF
const addHeader = (pdf, reportTitle, settings) => {
  // Company info from settings - comprehensive data extraction with sanitization
  const company = settings?.company || {};
  const companyName = sanitizeTextForPdf(company.name || company.companyName || 'Qawaem');
  const companyNameArabic = sanitizeTextForPdf(company.nameArabic || company.secondName || '');
  const companyActivity = sanitizeTextForPdf(company.activity || company.mainActivity || '');
  const companyServices = sanitizeTextForPdf(company.services || company.description || '');
  
  // Legal information (Saudi ZATCA identifiers with legacy fallbacks)
  const resolveCompanyField = (obj, primary, fallbacks = []) => {
    const primaryValue = obj?.[primary];
    if (primaryValue) return sanitizeTextForPdf(primaryValue);
    for (const key of fallbacks) {
      if (obj?.[key]) return sanitizeTextForPdf(obj[key]);
    }
    return '';
  };
  const crNo = resolveCompanyField(company, 'commercialRegistration', ['rc', 'rcNo']);
  const unifiedNo = resolveCompanyField(company, 'unifiedNationalNumber', ['nis', 'nisNo', 'articleNumber', 'articleNo', 'article']);
  const vatNo = resolveCompanyField(company, 'vatNumber', ['nif', 'nifNo']);
  const gosiNo = resolveCompanyField(company, 'gosiEstablishmentNumber', ['gosiNumber', 'gosi']);
  const chamberNo = sanitizeTextForPdf(company.chamberOfCommerceNumber || '');
  const municipalityNo = sanitizeTextForPdf(company.municipalityLicenseNumber || '');
  const zakatNo = sanitizeTextForPdf(company.zakatNumber || '');
  
  // Contact information (sanitized)
  const address = sanitizeTextForPdf(company.address || '');
  const city = sanitizeTextForPdf(company.city || company.wilaya || '');
  const postalCode = sanitizeTextForPdf(company.postalCode || company.codePostal || '');
  const phone = sanitizeTextForPdf(company.phone || company.telephone || '');
  const email = sanitizeTextForPdf(company.email || '');
  const website = sanitizeTextForPdf(company.website || company.siteWeb || '');

  const pageWidth = pdf.internal.pageSize.getWidth();
  let yPos = 10;

  // Company name (French)
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(companyName, 10, yPos);
  yPos += 6;

  // Company name (Arabic) if available - skip for PDF compatibility
  // Arabic text causes encoding issues in jsPDF, so we'll skip it for now
  // if (companyNameArabic) {
  //   pdf.setFontSize(12);
  //   pdf.setFont('helvetica', 'normal');
  //   pdf.text(companyNameArabic, 10, yPos);
  //   yPos += 5;
  // }

  // Activity and services
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  if (companyActivity) {
    pdf.text(companyActivity, 10, yPos);
    yPos += 4;
  }
  if (companyServices) {
    pdf.text(companyServices, 10, yPos);
    yPos += 4;
  }

  // Legal information section (right column)
  const rightColumnX = pageWidth / 2 + 10;
  let rightYPos = 10;
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  
  const legalInfo = [
    { label: i18n.t('common.commercial_registration'), value: crNo },
    { label: i18n.t('common.unified_national_number'), value: unifiedNo },
    { label: i18n.t('common.vat_number'), value: vatNo },
    { label: i18n.t('common.gosi_number'), value: gosiNo },
    { label: i18n.t('common.chamber_number'), value: chamberNo },
    { label: i18n.t('common.municipality_license'), value: municipalityNo },
    { label: i18n.t('common.zakat_number'), value: zakatNo },
  ];

  legalInfo.forEach((info) => {
    if (info.value) {
      pdf.text(`${info.label}: ${info.value}`, rightColumnX, rightYPos);
      rightYPos += 3.5;
    }
  });

  // Contact information (bottom of header)
  yPos = Math.max(yPos, rightYPos) + 2;
  
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  
  // Address line (without emojis to avoid encoding issues)
  const addressLine = [address, city, postalCode].filter(Boolean).join(', ');
  if (addressLine) {
    pdf.text(`${i18n.t('common.address_label')} ${addressLine}`, 10, yPos);
    yPos += 3;
  }
  
  // Contact line (without emojis to avoid encoding issues)
  const contactInfo = [phone, email, website].filter(Boolean).join(' | ');
  if (contactInfo) {
    pdf.text(`${i18n.t('common.contact_label')} ${contactInfo}`, 10, yPos);
    yPos += 4;
  }

  // Report title (centered)
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  const today = new Date().toLocaleDateString(reportLocale());
  const titleText = sanitizeTextForPdf(`${reportTitle} - ${today}`);
  
  // Center the title
  const textWidth = pdf.getStringUnitWidth(titleText) * 14 / pdf.internal.scaleFactor;
  pdf.text(titleText, (pageWidth - textWidth) / 2, yPos + 3);
  yPos += 6;

  // Decorative line under header
  pdf.setDrawColor(41, 128, 185); // Blue color
  pdf.setLineWidth(0.5);
  pdf.line(10, yPos, pageWidth - 10, yPos);
  
  // Lighter line below
  pdf.setDrawColor(220, 220, 220);
  pdf.setLineWidth(0.2);
  pdf.line(10, yPos + 1, pageWidth - 10, yPos + 1);
};

// Add footer to the PDF
const addFooter = (pdf, settings) => {
  const pageHeight = pdf.internal.pageSize.getHeight();
  const pageWidth = pdf.internal.pageSize.getWidth();
  
  // Add decorative line above the footer (matching header style)
  pdf.setDrawColor(41, 128, 185); // Blue color matching header
  pdf.setLineWidth(0.3);
  pdf.line(10, pageHeight - 20, pageWidth - 10, pageHeight - 20);
  
  // Add company info in footer
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  
  // Company details from settings - comprehensive extraction with sanitization
  const company = settings?.company || {};
  const companyName = sanitizeTextForPdf(company.name || company.companyName || 'Qawaem');
  const companyPhone = sanitizeTextForPdf(company.phone || company.telephone || '');
  const companyEmail = sanitizeTextForPdf(company.email || '');
  const companyWebsite = sanitizeTextForPdf(company.website || company.siteWeb || '');
  const address = sanitizeTextForPdf(company.address || '');
  const city = sanitizeTextForPdf(company.city || company.wilaya || '');
  
  // Professional footer layout
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  
  // Left side - Company name and location
  let footerY = pageHeight - 15;
  pdf.setFont('helvetica', 'bold');
  pdf.text(companyName, 10, footerY);
  
  pdf.setFont('helvetica', 'normal');
  if (address || city) {
    const locationText = [address, city].filter(Boolean).join(', ');
    pdf.text(locationText, 10, footerY + 3);
  }
  
  // Right side - Contact information
  pdf.setTextColor(80, 80, 80); // Dark gray for contact info
  if (companyPhone || companyEmail || companyWebsite) {
    const contactInfo = [companyPhone, companyEmail, companyWebsite].filter(Boolean).join(' | ');
    const contactWidth = pdf.getStringUnitWidth(contactInfo) * 8 / pdf.internal.scaleFactor;
    pdf.text(contactInfo, pageWidth - contactWidth - 10, footerY);
  }
  
  // Center - Generation timestamp
  pdf.setTextColor(120, 120, 120);
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(7);
  const generationText = i18n.t('common.report_generated_by', {
    date: new Date().toLocaleString(reportLocale()),
    app: 'Qawaem',
  });
  const genTextWidth = pdf.getStringUnitWidth(generationText) * 7 / pdf.internal.scaleFactor;
  pdf.text(generationText, (pageWidth - genTextWidth) / 2, pageHeight - 8);
  
  // Reset text color and add page number
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  const pageNumber = i18n.t('common.page_of', { current: 1, total: 1 });
  pdf.text(pageNumber, pageWidth - 20, pageHeight - 3);
};

